/**
 * <VideoGrid /> — os vídeos da chamada, com PALCO para quem compartilha tela.
 *
 * **O pedido (07/08/2026).** O Augusto: *"quando o compartilhamento da tela é
 * feito, seria interessante que ele ocupasse o maior espaço, seja em
 * dispositivos mobile, seja no desktop. O compartilhamento da tela deve ocupar
 * o maior espaço e as pessoas devem ficar … até o ícone das pessoas deve ficar
 * pequenininho."*
 *
 * Antes, tela compartilhada e rostos entravam na MESMA grade uniforme: com duas
 * pessoas e uma tela, cada um ficava com um terço do espaço — e a tela, que é o
 * único conteúdo com texto para ler, ficava do tamanho de um rosto.
 *
 * ## O layout
 *
 * Sem compartilhamento, tudo como antes: grade uniforme, todo mundo do mesmo
 * tamanho.
 *
 * Com compartilhamento, dois territórios:
 * - **palco** — a tela, ocupando o que sobrar do espaço;
 * - **faixa** — os rostos em miniatura, embaixo no celular e à direita no
 *   desktop, que é onde há espaço ocioso em cada formato.
 *
 * A faixa rola quando há gente demais, em vez de encolher os rostos até
 * ninguém se reconhecer.
 */

import { ParticipantTile, useTracks } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Mic } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

/** É desktop? (`lg` do Tailwind = 1024px)
 *
 *  A decisão de LAYOUT precisa disto em JavaScript, não só em CSS: no celular
 *  a estrutura muda (palco + faixa), não apenas o tamanho. Renderizar as duas
 *  estruturas e esconder uma com CSS duplicaria os elementos `<video>` de cada
 *  participante — caro, e com risco de o navegador decodificar duas vezes. */
function useTelaLarga(): boolean {
  const [larga, setLarga] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia("(min-width: 1024px)");
    const aoMudar = () => setLarga(mql.matches);
    aoMudar();
    mql.addEventListener("change", aoMudar);
    return () => mql.removeEventListener("change", aoMudar);
  }, []);
  return larga;
}

function gridClass(tileCount: number): string {
  if (tileCount <= 1) return "grid-cols-1";
  if (tileCount === 2) return "grid-cols-2";
  if (tileCount <= 4) return "grid-cols-2 grid-rows-2";
  return "grid-cols-3 auto-rows-fr";
}

/**
 * Stand-alone tile shown when a remote participant is audio-only or not
 * publishing camera yet. Pure visual fallback — does NOT subscribe to audio
 * tracks (LiveKitRoom handles audio playback transparently).
 */
function AudioOnlyTile({ identity }: { identity: string }) {
  const initial = identity.charAt(0).toUpperCase() || "?";
  return (
    <div
      data-testid="audio-only-tile"
      className="flex items-center justify-center bg-zinc-800 rounded-lg w-full h-full"
    >
      <div className="flex flex-col items-center gap-2 text-white">
        <div className="size-16 rounded-full bg-primary/40 flex items-center justify-center text-2xl font-semibold">
          {initial}
        </div>
        <div className="flex items-center gap-1 text-xs opacity-70">
          <Mic className="size-3" />
          <span className="truncate max-w-[120px]">{identity}</span>
        </div>
      </div>
    </div>
  );
}

/** Chave estável de um track — participante + origem. */
function chaveDo(trackRef: { participant: { identity: string }; source: unknown }): string {
  return `${trackRef.participant.identity}-${String(trackRef.source)}`;
}

export interface VideoGridProps {
  /** Modo COMPACTO: só o palco, sem faixa.
   *
   *  A miniatura tem 192×128. Uma faixa de rostos ali não caberia — e foi
   *  justamente uma sobreposição na miniatura que já quebrou os botões duas
   *  vezes. Em imagem-em-imagem se quer ver UMA coisa: o outro lado. */
  compacto?: boolean;
}

export function VideoGrid({ compacto = false }: VideoGridProps = {}) {
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  const telaLarga = useTelaLarga();

  /** Quem o usuário ESCOLHEU para o palco, por toque na faixa.
   *
   *  **O pedido do Augusto (09/08/2026):** *"ele não está mostrando como se
   *  fosse uma galeria para eu escolher o que eu quero ver."*
   *
   *  Sem escolha, quem manda é a heurística — e ela erra: numa chamada com
   *  tela compartilhada e duas pessoas, às vezes se quer ver o rosto de quem
   *  está falando, não a planilha.
   *
   *  Guarda a CHAVE e não o track: o objeto é recriado a cada publicação, e
   *  comparar por identidade perderia a escolha na primeira renegociação. */
  const [escolhido, setEscolhido] = useState<string | null>(null);

  if (tracks.length === 0) {
    return (
      <div
        data-testid="video-grid-empty"
        className="flex items-center justify-center w-full h-full text-white/60 text-sm"
      >
        Aguardando participantes…
      </div>
    );
  }

  // Só a PRIMEIRA tela vira palco. Duas pessoas compartilhando ao mesmo tempo é
  // raro e ambíguo — dividir o palco entre as duas deixaria ambas pequenas, que
  // é justamente o que este componente veio resolver. A segunda vai para a
  // faixa, visível e a um clique de virar palco quando a primeira parar.
  const telas = tracks.filter((t) => t.source === Track.Source.ScreenShare);

  // Sem tela compartilhada, com EXATAMENTE um remoto: ele vira palco e o meu
  // vídeo vira miniatura.
  //
  // **O que motivou (08/08/2026).** O Augusto, no celular: *"ficam 2 imagens
  // grandes … ocupando muito mais espaço do que o necessário; seria
  // interessante ter mais espaço para o vídeo."*
  //
  // A grade dividia 50/50. Num celular em RETRATO isso vira duas colunas
  // estreitas e altas — e como a câmera filma em paisagem, cada uma fica com
  // tarjas pretas em cima e embaixo. Metade da tela para mostrar um terço de
  // imagem, duas vezes.
  //
  // Conversa de duas pessoas tem um foco natural: o OUTRO. É o que Meet,
  // WhatsApp e Bitrix fazem — o interlocutor preenche, você aparece pequeno no
  // canto. Não é preferência estética: é o único arranjo em que a tela de um
  // telefone comporta um rosto em tamanho útil.
  const remotos = tracks.filter((t) => t.source === Track.Source.Camera && !t.participant.isLocal);
  const euMesmo = tracks.find((t) => t.source === Track.Source.Camera && t.participant.isLocal);
  // No CELULAR, palco + faixa vale para QUALQUER número de pessoas.
  //
  // **A preocupação do Augusto (09/08/2026):** *"quando entrar mais de uma
  // pessoa nessa chamada, principalmente no mobile, como é que ele vai se
  // comportar?"*
  //
  // A grade dava conta de 2. Com 3 ou mais num telefone em retrato, cada
  // célula vira um selo — ninguém reconhece ninguém. Palco + faixa rolável
  // escala para N: quem fala ocupa a tela, os demais ficam visíveis e
  // alcançáveis por rolagem.
  //
  // No desktop a grade continua boa: tela larga comporta 4 rostos com tamanho
  // útil, e ver todos ao mesmo tempo ajuda numa reunião.
  const temFoco = telas.length > 0 || remotos.length > 0;
  const usaPalco =
    compacto ||
    telas.length > 0 ||
    (!telaLarga && temFoco) ||
    (remotos.length === 1 && Boolean(euMesmo));

  const automatico = telas[0] ?? (usaPalco ? remotos[0] : undefined);
  // A escolha do usuário vence a heurística — mas só enquanto aquele
  // participante existir. Se ele sair, voltamos ao automático em vez de
  // mostrar um palco vazio.
  const palco = tracks.find((x) => chaveDo(x) === escolhido) ?? automatico;

  if (!palco) {
    return (
      <div
        data-testid="video-grid"
        className={`grid gap-2 w-full h-full p-2 ${gridClass(tracks.length)}`}
      >
        {tracks.map((trackRef) => (
          <ParticipantTile
            key={chaveDo(trackRef)}
            trackRef={trackRef}
            className="chamada-tile chamada-tile--cobrir rounded-lg bg-black overflow-hidden"
          />
        ))}
      </div>
    );
  }

  // Na miniatura, ninguém na faixa: só o palco. Ver `compacto`.
  const secundarios = compacto ? [] : tracks.filter((t) => t !== palco);

  return (
    <div
      data-testid="video-grid"
      data-com-tela={telas.length > 0 ? "true" : undefined}
      data-com-palco="true"
      // Celular empilha (tela em cima, rostos embaixo); desktop põe os rostos
      // numa coluna à direita, que é onde sobra espaço em tela larga.
      className="flex h-full w-full flex-col gap-2 p-2 lg:flex-row"
    >
      <div data-testid="palco-da-tela" className="min-h-0 min-w-0 flex-1">
        <ParticipantTile
          trackRef={palco}
          className={cn(
            "chamada-tile h-full w-full rounded-lg bg-black overflow-hidden",
            // TELA compartilhada usa `contain`: `cover` recorta as bordas, e
            // numa tela compartilhada a borda é conteúdo. O Augusto viu
            // exatamente isso — *"o compartilhamento ficou cortado, meio que
            // na vertical"*: uma imagem 16:9 recortada num contêiner alto.
            palco.source === Track.Source.ScreenShare
              ? "chamada-tile--conter"
              : "chamada-tile--cobrir"
          )}
        />
      </div>

      {secundarios.length > 0 && (
        <div
          data-testid="faixa-de-participantes"
          // Alturas/larguras fixas e pequenas: é isto que faz os rostos ficarem
          // "pequenininhos" e a tela ganhar o resto. `shrink-0` impede que o
          // palco os esmague a zero quando a janela é estreita.
          className="flex shrink-0 items-center gap-2 overflow-x-auto pb-1 lg:h-full lg:w-40 lg:flex-col lg:items-stretch lg:overflow-y-auto lg:overflow-x-hidden lg:pb-0"
        >
          {secundarios.map((trackRef) => (
            <button
              key={chaveDo(trackRef)}
              type="button"
              onClick={() => setEscolhido(chaveDo(trackRef))}
              title="Ver em tamanho grande"
              aria-label="Ver este participante em tamanho grande"
              data-testid="escolher-participante"
              className={cn(
                // ⚠️ O TAMANHO MORA AQUI, no elemento que NÓS criamos.
                //
                // Estava no `<ParticipantTile>`, e o print do Augusto mostrou a
                // miniatura com ~16×30 px em vez de 112×160: as classes não
                // chegavam ao elemento que define a caixa. O tile só tem
                // conteúdo posicionado de forma absoluta, então sem tamanho
                // explícito ele colapsa para quase nada.
                //
                // É a terceira vez hoje que confiar no DOM de terceiro me
                // custa uma rodada. Aqui o `<button>` é nosso: o tamanho é
                // garantido, e o tile apenas preenche.
                "h-40 w-28 lg:h-24 lg:w-full",
                "shrink-0 overflow-hidden rounded-lg transition",
                // Anel visível: sem ele a faixa parece decoração e ninguém
                // descobre que dá para tocar.
                "ring-2 ring-white/25 hover:ring-white/60",
                "focus:outline-none focus:ring-white/80"
                // NÃO há marca de "está no palco": quem está no palco SAI da
                // faixa, então a condição nunca casaria. Escrevi essa marca e
                // o teste a denunciou como estado impossível — código morto
                // que daria a impressão de recurso.
              )}
            >
              <ParticipantTile
                trackRef={trackRef}
                // Apenas PREENCHE — quem define a caixa é o botão acima.
                className="chamada-tile chamada-tile--cobrir h-full w-full rounded-lg bg-black overflow-hidden"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { AudioOnlyTile };
